package audio

import androidx.compose.runtime.staticCompositionLocalOf

/**
 * Platform-agnostic audio interface.
 *
 * Implementations live in platform source sets:
 * - [desktopMain] → JvmSoundEngine (javax.sound.sampled PCM synthesis)
 * - [jsMain]      → WebSoundEngine (Web Audio API)
 *
 * Provided to the composition tree via [LocalSoundEngine].
 */
interface SoundEngine {
    /** When false, all play calls are no-ops. */
    var enabled: Boolean

    fun playMove()
    fun playRotate()
    fun playSoftDrop()
    fun playHardDrop()
    fun playHold()
    fun playLock()
    fun playLineClear(lines: Int)
    fun playLevelUp()
    fun playGameOver()
}

/**
 * No-op fallback used until a real engine is injected, or when audio is
 * unavailable on the current platform.
 */
class NoOpSoundEngine : SoundEngine {
    override var enabled: Boolean = false
    override fun playMove() = Unit
    override fun playRotate() = Unit
    override fun playSoftDrop() = Unit
    override fun playHardDrop() = Unit
    override fun playHold() = Unit
    override fun playLock() = Unit
    override fun playLineClear(lines: Int) = Unit
    override fun playLevelUp() = Unit
    override fun playGameOver() = Unit
}

/**
 * CompositionLocal that provides the active [SoundEngine] to the entire
 * composition tree. Defaults to [NoOpSoundEngine] so callers never receive null.
 */
val LocalSoundEngine = staticCompositionLocalOf<SoundEngine> { NoOpSoundEngine() }
